import { useCallback, useEffect, useState } from "react";
import {
  KeyRound, ShieldCheck, ShieldOff, Loader2, ChevronRight, Copy, Printer, AlertTriangle,
  CheckCircle2, RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { TooltipButton } from "@/components/common/Tooltips";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const fecha = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-MX",
      { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return String(iso).slice(0, 19); }
};

export const PanelCodigosRespaldo = () => {
  const [abierto, setAbierto] = useState(false);
  const [estado, setEstado] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [busy, setBusy] = useState(null);
  const [codigos, setCodigos] = useState(null);   // visibles UNA sola vez
  const [confirmoResguardo, setConfirmoResguardo] = useState(false);

  const load = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await api.get("/admin/mfa/backup-codes");
      setEstado(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setCargando(false); }
  }, []);

  useEffect(() => { if (abierto) load(); }, [abierto, load]);

  const generar = async () => {
    setBusy("generar");
    try {
      const { data } = await api.post("/admin/mfa/backup-codes/generar",
        { confirmar_regeneracion: true });
      setCodigos(data);
      setConfirmoResguardo(false);
      toast.success(`${data.total} códigos generados. Se muestran una sola vez.`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(null); }
  };

  const activarSelf = async () => {
    setBusy("activar");
    try {
      const { data } = await api.post("/admin/mfa/self/activar", { confirmo_resguardo: true });
      toast.success(`2FA activado para tu cuenta. Aplica desde tu siguiente inicio de sesión. Códigos disponibles: ${data.backup_restantes}.`);
      setCodigos(null);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(null); }
  };

  const copiar = async () => {
    const txt = (codigos?.codigos || []).join("\n");
    try {
      await navigator.clipboard.writeText(txt);
      toast.success("Códigos copiados al portapapeles.");
    } catch { toast.error("No se pudo copiar. Selecciónalos y cópialos manualmente."); }
  };

  const imprimir = () => {
    const w = window.open("", "_blank");
    if (!w) { toast.error("El navegador bloqueó la ventana de impresión."); return; }
    w.document.write(`<!doctype html><html lang="es"><head><title>Códigos de respaldo · FELVORA</title>
      <style>body{font-family:Arial,Helvetica,sans-serif;padding:32px;color:#0f172a}
      h1{font-size:18px}code{display:block;font-size:18px;letter-spacing:2px;padding:6px 0}
      .n{color:#64748b;font-size:12px}</style></head><body>
      <h1>Códigos de respaldo del segundo factor · FELVORA Compliance</h1>
      <p class="n">Generados: ${fecha(codigos?.generado_at)} · Lote ${codigos?.lote_id || ""}</p>
      <p class="n">Cada código sirve UNA sola vez. Guárdalos en un lugar seguro y fuera de línea.</p>
      ${(codigos?.codigos || []).map((c, i) => `<code>${i + 1}. ${c}</code>`).join("")}
      </body></html>`);
    w.document.close();
    w.print();
  };

  const st = estado || {};
  const bajo = !!st.aviso_bajo;

  return (
    <section className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="panel-backup-codes">
      <button type="button" onClick={() => setAbierto((v) => !v)}
        data-testid="panel-backup-toggle"
        className="flex w-full items-center justify-between p-5 text-left">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100">
            <KeyRound className="h-5 w-5 text-amber-600" />
          </span>
          <div>
            <p className="font-heading text-sm font-semibold text-slate-900">
              Mi 2FA y códigos de respaldo (Super Administrador)
              {st.generados ? (
                <span data-testid="panel-backup-restantes-badge"
                  className={`ml-2 rounded-full px-2 py-0.5 text-xs font-semibold ${
                    bajo ? "bg-red-100 text-red-800" : "bg-emerald-100 text-emerald-800"}`}>
                  {st.restantes} de {st.total} disponibles
                </span>
              ) : (
                <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                  Sin generar
                </span>
              )}
            </p>
            <p className="text-xs text-slate-500">
              Puerta de entrada cuando el correo del código no llega. En base de datos solo se
              guarda el hash de cada código: nadie puede recuperarlos.
            </p>
          </div>
        </div>
        <ChevronRight className={`h-5 w-5 text-slate-400 transition-transform ${abierto ? "rotate-90" : ""}`} />
      </button>

      {abierto && (
        <div className="border-t border-slate-100 p-5" data-testid="panel-backup-body">
          {cargando ? (
            <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Mi 2FA</p>
                  <p className="mt-0.5 flex items-center gap-1.5 font-heading text-sm font-semibold"
                    data-testid="panel-backup-mi-2fa">
                    {st.mfa_activo
                      ? <><ShieldCheck className="h-4 w-4 text-emerald-600" /> Activo</>
                      : <><ShieldOff className="h-4 w-4 text-amber-600" /> Apagado</>}
                  </p>
                </div>
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Códigos sin usar</p>
                  <p className={`font-heading text-xl font-semibold ${bajo ? "text-red-600" : "text-slate-900"}`}
                    data-testid="panel-backup-restantes">
                    {st.generados ? `${st.restantes} / ${st.total}` : "—"}
                  </p>
                </div>
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Juego generado</p>
                  <p className="text-xs text-slate-700" data-testid="panel-backup-generado-at">
                    {st.generados ? fecha(st.generado_at) : "Nunca"}
                  </p>
                </div>
              </div>

              {bajo && (
                <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2.5 text-sm text-red-800"
                  data-testid="panel-backup-aviso-bajo">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                  <span>
                    Quedan <b>{st.restantes}</b> códigos de respaldo (umbral de aviso: {st.umbral_aviso}).
                    Regenera el juego completo para no quedarte sin puerta de emergencia.
                  </span>
                </div>
              )}

              {!st.generados && (
                <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900"
                  data-testid="panel-backup-sin-generar">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                  <span>
                    Todavía no has generado tus códigos de respaldo. <b>No se puede activar tu 2FA
                    sin ellos</b>: son la única forma de entrar si el correo falla.
                  </span>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2">
                <TooltipButton
                  tooltip={st.generados
                    ? "Genera un juego nuevo. Los 10 códigos anteriores quedan inválidos de inmediato."
                    : "Genera los 10 códigos de respaldo. Se muestran una sola vez."}
                  data-testid="panel-backup-generar" size="sm" disabled={busy === "generar"}
                  onClick={generar}
                  className={st.generados ? "" : "bg-primary hover:bg-sidebar-hover"}
                  variant={st.generados ? "outline" : "default"}>
                  {busy === "generar" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    : st.generados ? <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                      : <KeyRound className="mr-1.5 h-3.5 w-3.5" />}
                  {st.generados ? "Regenerar los 10 códigos" : "Generar mis 10 códigos"}
                </TooltipButton>

                {!st.mfa_activo && (
                  <TooltipButton
                    tooltip="Activa el 2FA de tu cuenta de Super Administrador. Requiere códigos de respaldo vigentes."
                    data-testid="panel-backup-activar-2fa" size="sm"
                    disabled={busy === "activar" || !st.puede_activar_2fa}
                    onClick={activarSelf}
                    className="bg-emerald-600 text-white hover:bg-emerald-700">
                    {busy === "activar" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      : <ShieldCheck className="mr-1.5 h-3.5 w-3.5" />}
                    Activar mi 2FA
                  </TooltipButton>
                )}
              </div>

              {!!(st.historial_usos || []).length && (
                <div>
                  <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Usos registrados en bitácora
                  </p>
                  <div className="overflow-hidden rounded-md border border-slate-200">
                    <table className="w-full text-left text-xs" data-testid="panel-backup-usos">
                      <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                        <tr>
                          <th className="px-2 py-1.5">Código #</th>
                          <th className="px-2 py-1.5">Fecha y hora</th>
                          <th className="px-2 py-1.5">IP</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {(st.historial_usos || []).map((u) => (
                          <tr key={`${u.indice}-${u.used_at}`} data-testid={`panel-backup-uso-${u.indice}`}>
                            <td className="px-2 py-1.5 font-mono text-slate-700">{u.indice}</td>
                            <td className="px-2 py-1.5 text-slate-600">{fecha(u.used_at)}</td>
                            <td className="px-2 py-1.5 font-mono text-slate-600">{u.ip || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Los códigos EN CLARO: única vez que existen fuera de la BD hasheada */}
      <Dialog open={!!codigos} onOpenChange={(v) => { if (!v) setCodigos(null); }}>
        <DialogContent className="max-w-lg" data-testid="backup-codes-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-amber-600" /> Tus {codigos?.total} códigos de respaldo
            </DialogTitle>
            <DialogDescription>
              Se muestran <b>una sola vez</b>. Cópialos o imprímelos ahora: en la base de datos
              solo queda su hash y no hay forma de recuperarlos.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-2 rounded-md border border-slate-200 bg-slate-50 p-3"
            data-testid="backup-codes-lista">
            {(codigos?.codigos || []).map((c, i) => (
              <div key={c} className="flex items-center gap-2 font-mono text-sm text-slate-900"
                data-testid={`backup-code-${i + 1}`}>
                <span className="w-4 text-right text-[10px] text-slate-400">{i + 1}</span>{c}
              </div>
            ))}
          </div>

          {!!codigos?.lotes_anteriores_invalidados && (
            <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
              data-testid="backup-codes-invalidados">
              Se invalidaron <b>{codigos.lotes_anteriores_invalidados}</b> código(s) del juego anterior.
              Ninguno de los viejos sirve ya.
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <Button data-testid="backup-codes-copiar" size="sm" variant="outline" onClick={copiar}>
              <Copy className="mr-1.5 h-3.5 w-3.5" /> Copiar
            </Button>
            <Button data-testid="backup-codes-imprimir" size="sm" variant="outline" onClick={imprimir}>
              <Printer className="mr-1.5 h-3.5 w-3.5" /> Imprimir
            </Button>
          </div>

          <label className="flex items-start gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700">
            <Checkbox data-testid="backup-codes-confirmo" checked={confirmoResguardo}
              onCheckedChange={(v) => setConfirmoResguardo(!!v)} className="mt-0.5" />
            <span>Confirmo que copié o imprimí los {codigos?.total} códigos y los guardé en un lugar seguro.</span>
          </label>

          <DialogFooter className="gap-2">
            <Button data-testid="backup-codes-cerrar" variant="outline" size="sm"
              onClick={() => setCodigos(null)}>Cerrar</Button>
            {!st.mfa_activo && (
              <Button data-testid="backup-codes-activar-2fa" size="sm"
                disabled={!confirmoResguardo || busy === "activar"}
                onClick={activarSelf}
                className="bg-emerald-600 text-white hover:bg-emerald-700">
                {busy === "activar" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  : <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />}
                Confirmar y activar mi 2FA
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};
